import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { BookingEducationApi } from '@core/apis/booking-education/booking-education.api';
import { BookingRehabilitationApi } from '@core/apis/booking-rehabilitation/booking-rehabilitation.api';
import { BookingRehabilitationProgramDTO } from '@core/apis/booking-rehabilitation/booking-rehabilitation.dto';
import { BookingServiceApi } from '@core/apis/booking-service/booking-service.api';
import { BookingStorageEnum } from '@core/enums/booking.enum';
import {
  RehabProgramBookingStatusEnum,
  ServiceBookingStatusEnum,
  OrderByEnum,
  PaymentTypeEnum,
} from '@oranta-mis/sdk';
import { PriceHelper } from '@core/helpers/price.helper';
import { TranslocoHelper } from '@core/helpers/transloco.helper';
import { IPaginationGet } from '@core/interfaces/interface';
import { FormatTimePipe } from '@core/pipes/format-time.pipe';
import { ReusableOrderMethodsService } from '@core/services/reusable-order-methods.service';
import { UserStore } from '@core/stores/user.store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IButtonClickEvent, ITableChangesConfig, ITableRow, Table, TableColumn } from '@oranta-mis/ui-library';
import { OrderDetailModalComponent } from '../order-detail-modal/order-detail-modal.component';
import { rehabilitationOrderListColumnHeader } from './rehabilitation';

interface IRenderTable extends ITableRow<BookingRehabilitationProgramDTO> {
  rehabilitation_program: string;
}

@Component({
  selector: 'app-rehabilitation',
  imports: [Table, OrderDetailModalComponent],
  standalone: true,
  templateUrl: './rehabilitation.component.html',
  styleUrl: './rehabilitation.component.scss',
})
@UntilDestroy()
export class RehabilitationOrderComponent extends ReusableOrderMethodsService {
  public columnHeader!: TableColumn[];
  public programHistoryPaginated!: IPaginationGet<BookingRehabilitationProgramDTO[]>;
  public programHistory: IRenderTable[] = [];

  @ViewChild(OrderDetailModalComponent)
  orderDetailModal!: OrderDetailModalComponent;

  private config!: ITableChangesConfig;

  constructor(
    protected override bookingRehabilitationApi: BookingRehabilitationApi,
    protected override bookingEducationApi: BookingEducationApi,
    protected override bookingServiceApi: BookingServiceApi,
    private readonly userStore: UserStore,
    private readonly i18n: TranslocoHelper,
  ) {
    super(bookingEducationApi, bookingRehabilitationApi, bookingServiceApi);
  }

  ngOnInit(): void {
    this.columnHeader = rehabilitationOrderListColumnHeader(this.i18n);
  }

  public setConfig(config: ITableChangesConfig): void {
    this.config = config;
    this.getList(config);
  }

  private getList(config: ITableChangesConfig): void {
    this.isLoading = true;
    let settings = {
      pagination: {
        page: config?.page,
        perPage: config?.perPage,
      },
      where: {
        patientId: this.userStore.get()?.id as number,
      },
      orderBy: {
        createdAt: OrderByEnum.desc,
      },
    };
    this.bookingRehabilitationApi
      .gets(settings)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (v) => {
          this.programHistory = this.renderObject(v.list);
          this.programHistoryPaginated = v;
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  public onActionHandler(event: IButtonClickEvent<IRenderTable>): void {
    switch (event.action) {
      case 'pay':
        this.pay(event.row.item.id, BookingStorageEnum.rehabilitation);
        break;
      case 'details':
        this.details(event.row.item);
        break;
      case 'cancel':
        this.cancel(event.row.item);
        break;
      default:
        break;
    }
  }

  public details(item: BookingRehabilitationProgramDTO): void {
    this.orderDetailModal.order = item;
    this.orderDetailModal.orderType = BookingStorageEnum.rehabilitation;
    this.orderDetailModal.program = item.rehabilitationProgram;
    this.orderDetailModal.openModal();
  }

  public cancel(item: BookingRehabilitationProgramDTO): void {
    this.isLoading = true;
    this.bookingRehabilitationApi
      .setStatus(item.id, {
        status: ServiceBookingStatusEnum.declined,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.getList(this.config),
        error: () => (this.isLoading = false),
      });
  }

  public renderObject(arr: BookingRehabilitationProgramDTO[]): IRenderTable[] {
    return arr.map((v) => {
      const { uid, clinic, rehabilitationProgram, startDate, updatedAt, status } = v;
      const { name, direction, complexity, price, paymentType, format, formatTime, hideDuration } =
        rehabilitationProgram;

      const datePipe = new DatePipe('en-US');
      const priceFormatted = new PriceHelper().centsToPrice(price);
      const durationFormatted = !hideDuration
        ? new FormatTimePipe(this.i18n).transform({
            format,
            formatTime,
          })
        : '-';
      const startDateFormatted = datePipe.transform(startDate, 'dd-MM-yyyy');
      const updatedAtFormatted = datePipe.transform(updatedAt, 'HH:mm, dd-MM-yyyy');

      return {
        id: v.id,
        item: v,
        rehabilitation_program: `<span class="badge bg-${v.status}">${this.i18n.tr(status)}</span>\n
		  <b><u>#${uid}</u></b>
		  <b>${clinic.longName}</b>
		  <b>${name}</b>\n
		  <u>${this.i18n.tr('direction')}</u>:&nbsp;${this.i18n.tr(direction)},
		  <u>${this.i18n.tr('complexity')}</u>:&nbsp;${this.i18n.tr(complexity)},
		  <u>${this.i18n.tr('price')}</u>:&nbsp;${priceFormatted}&nbsp;${this.i18n.tr('uah')},
		  <u>${this.i18n.tr('payment_type')}</u>:&nbsp;${this.i18n.tr(paymentType)},
		  <u>${this.i18n.tr('duration')}</u>:&nbsp;${durationFormatted},
		  <u>${this.i18n.tr('start_date')}</u>:&nbsp;${startDateFormatted},
		  <u>${this.i18n.tr('updated_at')}</u>:&nbsp;${updatedAtFormatted}`,
        buttonConfig: {
          pay: {
            action: 'pay',
            label: 'pay',
            icon: 'bi-credit-card',
            disabled: false,
            hidden: !(status === RehabProgramBookingStatusEnum.accepted && paymentType === PaymentTypeEnum.online),
          },
          cancel: { action: 'cancel', label: 'cancel', icon: 'bi-x-circle', disabled: false, hidden: false },
          details: { action: 'details', label: 'details', icon: 'bi-info-circle', disabled: false, hidden: false },
        },
      };
    });
  }
}
